import client from "./client";

// -------------------- Types --------------------

export type LifecycleEvent = "ONBOARDING" | "OFFBOARDING";
export type LifecycleInstanceStatus = "IN_PROGRESS" | "COMPLETED" | "CANCELLED";
export type LifecycleTaskCategory =
  | "ACCESS"
  | "EQUIPMENT"
  | "KNOWLEDGE"
  | "BENEFITS"
  | "FINAL_PAY"
  | "EXIT"
  | "PAPERWORK"
  | "TRAINING"
  | "INTRODUCTION"
  | "ADMIN"
  | "OTHER";
export type TaskStatus =
  | "OPEN"
  | "IN_PROGRESS"
  | "BLOCKED"
  | "DONE"
  | "CANCELLED";

export interface LifecycleInstance {
  id: string;
  org_id: string;
  employee_id: string;
  template_id: string | null;
  event: LifecycleEvent;
  status: LifecycleInstanceStatus;
  total_tasks: number;
  completed_tasks: number;
  started_at: string;
  completed_at: string | null;
}

export interface LifecycleTask {
  id: string;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: string;
  assignee_id: string | null;
  due_date: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface OffboardingSummary {
  active: number;
  completed_last_30d: number;
  overdue_tasks: number;
  due_soon_tasks: number;
}

// -------------------- API --------------------

export async function listInstances(params?: {
  employee_id?: string;
}): Promise<LifecycleInstance[]> {
  const res = await client.get<LifecycleInstance[]>("/lifecycle/instances", {
    params,
  });
  return res.data;
}

export async function getInstance(id: string): Promise<LifecycleInstance> {
  const res = await client.get<LifecycleInstance>(`/lifecycle/instances/${id}`);
  return res.data;
}

export async function listInstanceTasks(id: string): Promise<LifecycleTask[]> {
  const res = await client.get<LifecycleTask[]>(
    `/lifecycle/instances/${id}/tasks`
  );
  return res.data;
}

export async function toggleTask(
  instanceId: string,
  taskId: string,
  isComplete: boolean
): Promise<LifecycleTask> {
  const res = await client.patch<LifecycleTask>(
    `/lifecycle/instances/${instanceId}/tasks/${taskId}`,
    { is_complete: isComplete }
  );
  return res.data;
}

export interface AddTaskBody {
  title: string;
  description?: string;
  category?: LifecycleTaskCategory;
  due_date?: string;
  assignee_id?: string;
}

export async function addCustomTask(
  instanceId: string,
  body: AddTaskBody
): Promise<LifecycleTask> {
  const res = await client.post<LifecycleTask>(
    `/lifecycle/instances/${instanceId}/tasks`,
    body
  );
  return res.data;
}

export async function updateInstance(
  id: string,
  body: { status?: LifecycleInstanceStatus }
): Promise<LifecycleInstance> {
  const res = await client.patch<LifecycleInstance>(
    `/lifecycle/instances/${id}`,
    body
  );
  return res.data;
}

export async function cancelInstance(id: string): Promise<void> {
  await client.delete(`/lifecycle/instances/${id}`);
}

export async function getOffboardingSummary(): Promise<OffboardingSummary> {
  const res = await client.get<OffboardingSummary>(
    "/lifecycle/offboarding/summary"
  );
  return res.data;
}
